import React from 'react';
import { useTranslation } from 'react-i18next';

import ActivationTrendChart from '../../components/ActivationTrendChart/ActivationTrendChart';
import Dropdown from '../../components/Dropdown/Dropdown';
import { TREND_WINDOW_OPTIONS } from '../../common/activationTrend.js';
import './DangerousWasteDashboardPane.css';

const GROUP_ROW_COLORS = [
    '#74D5F2',
    '#BD92F4',
    '#81F3D5',
    '#FF8989',
    '#81ECF3',
    '#53C0FF',
    '#ED9BD2',
    '#FF89B0',
    '#90AAFF',
    '#81F3AD',
];

function DangerousWasteDashboardPane({
    activationCount,
    hasGroupingColumns,
    recordGroups,
    activationTrend,
    isTrendLoading,
    trendWindowDays,
    onTrendWindowDaysChange,
    datePicker,
}) {
    const { t } = useTranslation();

    const selectedTrendWindow = TREND_WINDOW_OPTIONS.find((option) => option.days === trendWindowDays)
        || TREND_WINDOW_OPTIONS[0];
    const hasTrendActivations = activationTrend.some((day) => day.count > 0);

    return (
        <div className="DangerousWasteDashboardPane">
            <div className="dashboard-pane-wrapper">
                <div className="card-container activations-card">
                    <div className="card-header">
                        <div className="card-header-title">{t('Aktiveringer')}</div>
                        {datePicker}
                    </div>
                    <div className="card-body activations-body">
                        <p className="activations-total">
                            <span className="activations-total-label">{t('Total brug')}:</span>
                            <span className="activations-total-value">{activationCount}</span>
                        </p>
                    </div>
                </div>

                {/* The title names this card's own window, since it is shorter than the
                    selected period and its bars will not sum to the total above. */}
                <div className="card-container trend-card">
                    <div className="card-header">
                        <div className="card-header-title">
                            {`${t('Aktiveringer')} — ${t(selectedTrendWindow.titleSuffix)}`}
                        </div>
                        <Dropdown
                            options={TREND_WINDOW_OPTIONS.map((option) => t(option.label))}
                            selectedOption={t(selectedTrendWindow.label)}
                            setSelectedOption={(label) => {
                                const picked = TREND_WINDOW_OPTIONS.find((option) => t(option.label) === label);
                                if (picked) onTrendWindowDaysChange(picked.days);
                            }}
                        />
                    </div>
                    <div className="card-body trend-body">
                        {isTrendLoading && (
                            <div className="trend-empty-state">{t('Indlæser')}...</div>
                        )}
                        {!isTrendLoading && !hasTrendActivations && (
                            <div className="trend-empty-state">{t('Ingen aktiveringer at vise')}</div>
                        )}
                        {!isTrendLoading && hasTrendActivations && (
                            <ActivationTrendChart trend={activationTrend} />
                        )}
                    </div>
                </div>

                <div className="card-container groups-card">
                    <div className="card-header">
                        <div className="card-header-title">{t('Grupperinger')}</div>
                    </div>
                    <div className="card-body groups-body">
                        {!hasGroupingColumns && (
                            <div className="groups-empty-state">
                                {t('Ingen grupperingskolonner valgt. Kontakt ARIS')}
                            </div>
                        )}
                        {hasGroupingColumns && recordGroups.length === 0 && (
                            <div className="groups-empty-state">
                                {t('Ingen registreringer i perioden')}
                            </div>
                        )}
                        {hasGroupingColumns && recordGroups.length > 0 && (
                            <div className="group-rows">
                                {recordGroups.map((group, index) => (
                                    <div
                                        key={group.key}
                                        className="group-row"
                                        style={{ borderLeft: `2px solid ${GROUP_ROW_COLORS[index % GROUP_ROW_COLORS.length]}` }}
                                    >
                                        <div className="group-row-cells">
                                            {group.cells.map((cell) => (
                                                <div key={cell.id} className="group-row-cell">
                                                    <div className="group-row-cell-label">{t(cell.label)}</div>
                                                    <div className="group-row-cell-value">{t(cell.value)}</div>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="group-row-count">
                                            <span>{Math.round(group.share * 100)}%</span>
                                            <span>|</span>
                                            <span>{group.count}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default DangerousWasteDashboardPane;
